// Inline form for adding or editing a reference type (name + optional
// description + enabled toggle). Used both above the list (Add) and
// inside ReferenceTypeCard (Edit).
import { useState, type FormEvent } from "react";
import { api } from "../../../lib/api";
import type { ReferenceTypeDto } from "./ReferenceLibrary";

interface Props {
  initial?: ReferenceTypeDto;
  onSaved: (type: ReferenceTypeDto) => void;
  onCancel: () => void;
}

export function ReferenceTypeForm({ initial, onSaved, onCancel }: Props) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name,
        description: description || null,
        ...(initial ? { enabled } : {}),
      };
      const saved = initial
        ? await api<ReferenceTypeDto>(
            `/api/tools/pipe-takeoff/references/${initial.id}`,
            { method: "PATCH", json: payload },
          )
        : await api<ReferenceTypeDto>("/api/tools/pipe-takeoff/references", {
            method: "POST",
            json: payload,
          });
      onSaved(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bsp-card" style={{ display: "grid", gap: 12, marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div className="bsp-label">Name</div>
          <input
            className="bsp-input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Elbow, Tee, Flange"
            disabled={busy}
          />
        </div>
        {initial && (
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={busy}
            />
            <span>Enabled</span>
          </label>
        )}
      </div>
      <div>
        <div className="bsp-label">Description (optional)</div>
        <textarea
          className="bsp-input"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Short note describing this component type."
          disabled={busy}
          style={{ width: "100%", fontFamily: "inherit", resize: "vertical" }}
        />
      </div>

      {error && (
        <p style={{ color: "var(--bsp-red-fg)", margin: 0, fontSize: 12 }}>{error}</p>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" className="bsp-btn bsp-btn-primary" disabled={busy}>
          {busy ? "Saving…" : initial ? "Save" : "Add type"}
        </button>
        <button type="button" className="bsp-btn bsp-btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
